/**
 * The frontend's ONE mirror of the plans: bumble-bee app/models/subscription.py TIER_CONFIG (chat v5, CONTRACT5 §1).
 *
 * Every plan has two measures, and nothing else is metered:
 *   CHAT     a monthly amount, shown as a bar. The server keeps it in money and never sends that; a plan is described
 *            as "about N questions a month" (the bar is the truth, N is always "about").
 *   REPORTS  a set number a month: an offer review, a shortlist of brands, or a file on one brand. One run that delivers
 *            is one report; one that files nothing is free.
 * Run out of either, and a creator can wait, move up a plan, or add a little more (EXTRAS, one-off, never a
 * subscription). There is no "unlimited" anywhere: the founder's rule.
 *
 * Each plan says ONE thing it lets a creator do (`does`); the two measures say how much. Nothing else is sold on a
 * card: every plan runs the deal desk (EVERY_PLAN_LINE), and a line only differs between plans when a gate differs.
 *
 * Inside the app the server wins wherever it is known: prices, "about N questions" and reports read `allTiers`
 * (GET /tiers) first and fall back to these numbers only while that fetch hasn't landed. The public landing page has no
 * SubscriptionProvider, so it prints these. A creator's own usage never comes from here: that is the usage summary.
 * tierConfig.test.ts pins every number below to the backend's TIER_CONFIG, so a drift fails CI.
 *
 * Pure data: no React, no context, so the public landing page can import it.
 */
import type { SubscriptionTier, TierInfo } from '../../contexts/SubscriptionContext';

export interface TierLimitsMirror {
  /** New creators added to Bearri a month (-1: no cap). Only FAQ 8 says it; never a card. */
  saved_creators: number;
  /** Reports a month. */
  reports: number;
}

export interface TierConfig {
  name: string;
  /** Monthly price in USD; -1 = set up with you (Studio). */
  price: number;
  /** One line: what the plan lets a creator do. */
  does: string;
  /** About how many questions a month's chat holds (the words; the bar is exact). */
  questions: number;
  /** What the plan carries over from the one below it (Pro only: the do-line carries the rest). */
  features: string[];
  limits: TierLimitsMirror;
}

/** What a report is, said once under the plans (never on each card). */
export const REPORT_KINDS_LINE = 'A report is an offer review, a shortlist of brands, or a file on one brand.';

/** Said once above the plans: what no plan takes away. deals.py gates only the offer read; links and deal links have
 *  no tier gate at all. */
export const EVERY_PLAN_LINE = 'On every plan: counter offers, deal links, tracked links and a results log. Bearri takes 0% of your deals.';

/** The Studio door: sales-touched, never a checkout. One mailto for every "Talk to us". */
export const STUDIO_CONTACT = 'mailto:support@bearri.co?subject=Studio%20plan';

/** One-off extras (CONTRACT5 §4). The server's `extra_offers` / `/tiers` extras win wherever they are known. */
export const EXTRAS_MIRROR = {
  chat: { price_usd: 5, adds: 'about 50 questions' },
  reports: { price_usd: 10, adds: '5 reports' },
} as const;

const count = (n: number): string => n.toLocaleString('en-US');

export const TIER_CONFIG: Record<SubscriptionTier, TierConfig> = {
  free: {
    name: 'Free',
    price: 0,
    // limits.reports = 2, shared by the three kinds (hence "up to"); the deal desk itself is ungated.
    does: 'Run your deals here, and review up to two offers a month.',
    questions: 10,
    features: [],
    limits: { saved_creators: 25, reports: 2 },
  },
  plus: {
    name: 'Plus',
    price: 17,
    // data.py withholds found contacts below Plus (unlock_tier "plus"); share.py requires Plus for a media-kit link.
    does: 'Find who to email at a brand, and send your media kit as a link.',
    questions: 120,
    features: [],
    limits: { saved_creators: 100, reports: 10 },
  },
  pro: {
    name: 'Pro',
    price: 40,
    // Pro's headline rests ONLY on room: 30 reports (about one a day) and about 300 questions. Never a model, alerts,
    // platforms or exports: none of those is enforced.
    does: 'Room for a busy inbox: an offer review most days.',
    questions: 300,
    features: ['Everything in Plus'],
    limits: { saved_creators: 500, reports: 30 },
  },
  studio: {
    name: 'Studio',
    price: -1,
    // seats() returns 1: Studio is one creator, set up with you. Never "several creators" or "per creator".
    does: 'Need more than Pro? Set up with you.',
    questions: 1000,
    features: [],
    limits: { saved_creators: -1, reports: 100 },
  },
};

/** The self-serve ladder. Studio is sales-touched: never an upgrade card, at most "Talk to us". */
export const SELF_SERVE: SubscriptionTier[] = ['free', 'plus', 'pro'];
const LADDER: SubscriptionTier[] = ['free', 'plus', 'pro', 'studio'];

/** A plan's place on the ladder (free 0 < plus 1 < pro 2 < studio 3): the one ranking every move up/down reads. */
export const tierRank = (tier: SubscriptionTier | null | undefined): number => (tier ? LADDER.indexOf(tier) : 0);

const serverTier = (tier: SubscriptionTier, serverTiers?: TierInfo[]) => serverTiers?.find((t) => t.tier === tier);

/** A plan's price: the server's (GET /tiers) when it has landed, else the mirror's. */
export function tierPrice(tier: SubscriptionTier, serverTiers?: TierInfo[]): number {
  const p = serverTier(tier, serverTiers)?.price_monthly;
  return typeof p === 'number' ? p : TIER_CONFIG[tier].price;
}

/** A plan's name, as the server spells it. */
export function tierName(tier: SubscriptionTier, serverTiers?: TierInfo[]): string {
  return serverTier(tier, serverTiers)?.display_name || TIER_CONFIG[tier].name;
}

/** "about 120 questions a month" (the server's words when known). A trailing " per creator" from an older server is
 *  dropped: Studio is one creator. */
export function questionsNote(tier: SubscriptionTier, serverTiers?: TierInfo[]): string {
  const note = serverTier(tier, serverTiers)?.questions_note;
  if (typeof note === 'string' && note) return note.replace(/ per creator$/, '');
  return `about ${count(TIER_CONFIG[tier].questions)} questions a month`;
}

/** About how many questions a plan's month holds, as a number (the plan sheet's strip length). */
export function questionsAbout(tier: SubscriptionTier, serverTiers?: TierInfo[]): number {
  const m = /about ([\d,]+) questions/i.exec(questionsNote(tier, serverTiers));
  const n = m ? Number(m[1].replace(/,/g, '')) : NaN;
  return Number.isFinite(n) && n > 0 ? n : TIER_CONFIG[tier].questions;
}

/** Reports a month on a plan (the server's number when known). */
export function reportsPerMonth(tier: SubscriptionTier, serverTiers?: TierInfo[]): number {
  const n = serverTier(tier, serverTiers)?.limits?.reports;
  return typeof n === 'number' && n >= 0 ? n : TIER_CONFIG[tier].limits.reports;
}

/** The plan a creator upgrades to: the server's `next_tier` when known; Free → Plus → Pro; nothing past Pro. */
export function nextTierOf(tier: SubscriptionTier | undefined, serverTiers?: TierInfo[]): SubscriptionTier | null {
  const from = tier || 'free';
  const server = serverTier(from, serverTiers);
  if (server && 'next_tier' in server) return (server.next_tier as SubscriptionTier | null | undefined) ?? null;
  const i = LADDER.indexOf(from);
  const next = LADDER[i + 1];
  return next && next !== 'studio' ? next : null;
}

/** "About 300 questions and 30 reports a month" — what a plan holds, in the words the limit cards use. */
export function planHolds(tier: SubscriptionTier, serverTiers?: TierInfo[]): string {
  const note = questionsNote(tier, serverTiers).replace(/ a month$/, '');
  const n = reportsPerMonth(tier, serverTiers);
  return `${note.charAt(0).toUpperCase()}${note.slice(1)} and ${count(n)} reports a month`;
}

/** planHolds mid-sentence: "about 300 questions and 30 reports a month". */
export const planHoldsLower = (tier: SubscriptionTier, serverTiers?: TierInfo[]): string => {
  const s = planHolds(tier, serverTiers);
  return s.charAt(0).toLowerCase() + s.slice(1);
};
